import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

import {
  Target,
  Zap,
  Award,
  Star,
  Lightbulb,
  Globe,
  Shield,
} from 'lucide-react';

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About Us - PhysicsNotebook.in</title>
        <meta name="description" content="Learn about PhysicsNotebook.in - A premier educational platform dedicated to making physics accessible, engaging, and effective for students worldwide." />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-blue-50 to-white py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-display text-4xl md:text-5xl font-semibold text-gray-900 mb-6"
          >
            Physics, Taught the Way It Should Be
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            We mentor students to master physics through smarter learning strategies and guidance built around them — not a generic syllabus.
          </motion.p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl font-semibold text-gray-900 mb-4 flex items-center justify-center gap-3">
              <Target className="w-8 h-8 text-[#123B7A]" />
              Our Mission
            </h2>
            <div className="w-24 h-1 bg-[#123B7A] mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              To mentor students in mastering physics through smart learning strategies — building strong conceptual clarity, speed, and accuracy needed to excel in board and competitive exams.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 mt-16">
            <div className="text-center p-6 rounded-xl hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-blue-50 to-white border border-blue-100 transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <Target className="w-8 h-8 text-[#123B7A]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Mentorship That Trains You to Score</h3>
              <p className="text-gray-700">Our mentors don't just explain concepts — they train you to recognize question patterns and apply the right approach fast, using frameworks refined across hundreds of students.</p>
            </div>

            <div className="text-center p-6 rounded-xl hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-green-50 to-white border border-green-100 transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <Zap className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Strategies Toppers Actually Use</h3>
              <p className="text-gray-700">Formula-based approaches and concept maps that make recall automatic — so under exam pressure, you're retrieving a method, not reconstructing it from scratch.</p>
            </div>

            <div className="text-center p-6 rounded-xl hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-purple-50 to-white border border-purple-100 transform hover:-translate-y-1">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                <Award className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Progress You Can See</h3>
              <p className="text-gray-700">Every session ties back to measurable improvement — so you're not just hoping it's working, you can see exactly where you've gotten stronger.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-display text-3xl font-semibold text-gray-900 mb-6">Our Story</h2>
              <div className="w-24 h-1 bg-[#123B7A] mb-8"></div>
              <p className="text-gray-600 mb-6">
                Physics Notebook began with a simple idea: real learning happens through understanding, not mere instruction. Traditional classroom teaching often stops at explanations — but mastering physics takes deeper thinking, guided practice, and the right mindset.
              </p>
              <p className="text-gray-600 mb-6">
                So we built something different. Our mentor-led approach focuses on helping students learn how to learn — developing clarity, confidence, and problem-solving skills that make even the toughest chapters feel manageable.
              </p>
              <p className="text-gray-600 mb-6">
                Along the way, our mentors have helped students move from rote memorization to real comprehension — changing not just their marks, but how they think through a problem.
              </p>
              <p className="text-gray-600">
                Today, Physics Notebook combines structured learning strategies, exam-smart guidance, and personalized mentoring — all aimed at understanding that actually lasts.
              </p>
            </div>
            <div className="rounded-2xl p-10 flex flex-col items-center justify-center text-center" style={{ background: 'linear-gradient(135deg, #EAF2FF 0%, #F6F8FC 100%)' }}>
              <img src="/brand/icon-transparent.png" alt="Physics Notebook" className="h-20 w-auto mb-6" />
              <p className="font-display text-xl text-gray-800 leading-snug">
                "Real learning happens through understanding — not instruction alone."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-display text-3xl font-semibold text-gray-900 mb-4">Our Core Values</h2>
            <div className="w-24 h-1 bg-[#123B7A] mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Excellence',
                description: 'We strive for the highest standards in educational content and user experience.',
                icon: Star,
                color: 'bg-yellow-50 text-yellow-600'
              },
              {
                title: 'Innovation',
                description: 'We continuously explore new ways to enhance learning through technology and pedagogy.',
                icon: Lightbulb,
                color: 'bg-blue-50 text-[#123B7A]'
              },
              {
                title: 'Accessibility',
                description: 'We believe quality physics education should be available to all students, everywhere.',
                icon: Globe,
                color: 'bg-green-50 text-green-600'
              },
              {
                title: 'Integrity',
                description: 'We maintain the highest standards of academic integrity and ethical conduct.',
                icon: Shield,
                color: 'bg-purple-50 text-purple-600'
              }
            ].map((value, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
              >
                <div className={`w-12 h-12 ${value.color} rounded-full flex items-center justify-center mb-4 mx-auto`}>
                  <value.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-center text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 text-center">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 dark-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-semibold mb-6">Ready to Learn Physics Differently?</h2>
          <p className="text-xl mb-8 text-gray-300">Join the students who've swapped rote memorization for real understanding — and a mentor who tells them exactly where they stand.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/solutions" 
              className="bg-white text-[#123B7A] hover:bg-blue-50 font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Explore Our Solutions
            </a>
            <a
              href="/contact"
              className="border-2 border-white hover:bg-white hover:text-[#123B7A] font-semibold py-3 px-8 rounded-lg transition-colors duration-200"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
